/**
 * Purpose: Fee releated selectors.
 */

// libraries
import type { GovernmentFees } from '@makemydeal/dr-platform-shared';
import type {
    IDealerFee,
    IPencilOfferInputsDealerFee,
    OfferType,
    PaymentTerm,
    FeeOverride,
    ItemizedGovernmentFee
} from '@makemydeal/dr-platform-types';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';

import { CASH, LEASE } from '@makemydeal/dr-platform-types';

// types
import type { FeeItem } from '@makemydeal/dr-platform-types/typedefs/shared/types/paymentServices';

// utils
import {
    getCurrentOfferType,
    getIsUserProgramQuotes,
    getOfferDetails,
    getOfferPrice,
    getOfferTerm,
    getSelectedTerm,
    getSelectedTermByOfferType,
    getTerm,
    getTermIdForOfferType,
    getTradeInAmountOwed,
    getTradeInValue,
    shouldIncludeTradeIn
} from './offerNodeSelectors';
import { getDealerAcquisitionFeeFromTerm } from './slices/termSliceSelectors';
import { getAllIncentiveTotal, getAppliedIncentivesTotalForDisplay } from './incentiveSelectors';
import { isUpqAllInPriceIncentivesEnabled } from '../utils/incentives';
import { createSelector } from 'reselect';
import { FeesCategoriesType } from '../constants/payment';
import { getDiaDueAtSigning, getPaidAtSigning } from './selectedTermSelectors';

declare type FeesItem = FeeItem & IDealerFee;

export const getIncludedInCalc = (fees: FeesItem[]): FeesItem[] => {
    return fees?.filter((fee: FeeItem) => fee.includedInCalc || fee.isFeeIncludedInCalculation) || [];
};

export function getDealerFeesFromTerm(term: PaymentTerm, filter: boolean = true): IDealerFee[] {
    const dealerFees = filter ? getIncludedInCalc(term?.dealerFees) : term?.dealerFees;
    return (dealerFees && dealerFees.filter((fee: any) => fee.originalCategory?.toLowerCase() === 'dealer')) || [];
}

export function getDealerLenderFeesFromTerm(term: PaymentTerm, filter: boolean = true): IDealerFee[] {
    return filter ? getIncludedInCalc(term?.dealerFees) : term?.dealerFees;
}

export function getDealerFeesTotalFromTerm(term: PaymentTerm) {
    const dealerFees = getDealerFeesFromTerm(term);
    return dealerFees.length > 0 ? dealerFees.reduce((acc, curr) => acc + curr.dealerFeeAmount, 0) : 0;
}

export function getDealerFees(state: OfferReduxBaseState) {
    // uses same logic as getMonthlyPayment() to get current payment's dealer fees
    const selectedTerm = getSelectedTerm(state);
    return getDealerFeesFromTerm(selectedTerm);
}

/**
 * Returns the included dealer fees for the current offer type.
 * Finance/Lease: payment-term fees are IDealerFee[] carrying `includedInCalc`/`originalCategory`,
 * so we keep only the dealer-category fees flagged as included in the calculation.
 * Cash: cash offer-detail fees are IPencilOfferInputsDealerFee[] with no `includedInCalc`/
 * `originalCategory` flags and no lender fees, so every listed fee is an included dealer fee.
 * The union return type reflects that CASH items do not carry the finance/lease flags, so callers
 * can't read `includedInCalc`/`originalCategory` on a result without narrowing first.
 */
export function getIncludedDealerFees(state: OfferReduxBaseState): IDealerFee[] | IPencilOfferInputsDealerFee[] {
    const offerType = getCurrentOfferType(state);
    if (offerType === CASH) {
        return getOfferDetails(state, CASH).dealerFees || [];
    }
    const fees: IDealerFee[] = getSelectedTerm(state).dealerFees || [];
    return fees.filter((fee) => fee.includedInCalc && fee.originalCategory?.toLowerCase() === 'dealer');
}

/**
 * Returns the "All-in Price": offer price less all applied incentives, plus the total dealer fees
 * (originalCategory "Dealer", includedInCalc true), rounded up to the nearest dollar (Math.ceil,
 * consistent with taxesAndFeesTotal). The offer price is the post-discount asking price and does NOT
 * include incentives, so we subtract the full incentive total (non-conditional manufacturer incentives
 * plus applied conditional incentives) to match the "Your Price" the shopper actually pays. Uses the
 * pre-computed totalDealerFees field from the payment response instead of re-summing. On user-program-quotes
 * with DE773365_Enable_UPQ_All_In_Price_Incentives on, the incentive total is getAppliedIncentivesTotalForDisplay.
 * A saved incentiveTotalOverride does not change it: the payment lambda prices manualIncentives.rebates over it.
 */
export function getDealerFeesAllInPrice(state: OfferReduxBaseState): number {
    const offerType = getCurrentOfferType(state);
    const offerPrice = getOfferPrice(state) || 0;
    const totalIncentives =
        isUpqAllInPriceIncentivesEnabled(state) && getIsUserProgramQuotes(state, offerType)
            ? getAppliedIncentivesTotalForDisplay(state, offerType)
            : getAllIncentiveTotal(state) || 0;
    // The pre-computed total lives under different field names: payment terms expose `totalDealerFees`,
    // while cash offer details expose `dealerFeesTotal` (see getTotalUnifiFees / getTaxesAndFeesTotal).
    // Both getOfferDetails and getSelectedTerm always return an object, so no optional chaining is needed.
    const totalDealerFees =
        offerType === CASH ? getOfferDetails(state, CASH).dealerFeesTotal ?? 0 : getSelectedTerm(state).totalDealerFees ?? 0;
    return Math.ceil(offerPrice - totalIncentives + totalDealerFees);
}

/**
 * Returns the pre-computed total of the included dealer fees for the current offer type.
 * Payment terms (finance/lease) expose this as `totalDealerFees`; cash offer details expose it as
 * `dealerFeesTotal` (see getTotalUnifiFees / getTaxesAndFeesTotal). This is the same figure summed by
 * the itemized fees from getIncludedDealerFees, surfaced directly so consumers don't re-sum in the UI.
 */
export function getIncludedDealerFeesTotal(state: OfferReduxBaseState): number {
    const offerType = getCurrentOfferType(state);
    // Both getOfferDetails and getSelectedTerm always return an object, so no optional chaining is needed.
    return offerType === CASH ? getOfferDetails(state, CASH).dealerFeesTotal ?? 0 : getSelectedTerm(state).totalDealerFees ?? 0;
}

export const getSelectedTermDealerFees = createSelector([getOfferTerm], (term) => term?.dealerFees);

export const getItemizedLenderFees = createSelector(
    [getSelectedTermDealerFees],
    (dealerFees): IDealerFee[] => dealerFees?.filter((fee: IDealerFee) => fee.originalCategory?.toLowerCase() === 'lender') || []
);

/**
 * Mirrors the fee editor's inclusion rule (activities-desking src/components/fees/utils.ts): an
 * explicit isFeeIncludedInCalculation wins, includedInCalc is the legacy fallback, and a fee carrying
 * neither flag counts as included. Do not collapse this into an OR of the two flags -- that would drop
 * a flag-less lender fee that the untoggled total counts today. `getIncludedInCalc` above shares the
 * editor helper's name but not its rule: it is that OR form, answers a different question, and the two
 * are not interchangeable.
 */
export const countsInCalculation = (fee: IDealerFee): boolean => fee.isFeeIncludedInCalculation ?? fee.includedInCalc ?? true;

/**
 * DE773776: the Deal Breakdown itemizes only the lender fees that count toward the total, and the
 * Print Proposal has to render the same set or the two documents disagree. Both surfaces apply the
 * rule through here so there is one copy of it; each passes its own toggle state, because the
 * breakdown resolves toggles through dr-shared-store, which offer-redux cannot import (this lib is
 * tagged scope:bff, and that dep constraint forbids it), while here we read `state.featureToggles`.
 *
 * The caller chooses the set: the breakdown hands in an already lender-only list, while the
 * proposal's list is mixed and narrows to lender fees itself, since dealer and government fees have
 * their own display rules.
 */
export const filterCountedLenderFees = (fees: IDealerFee[], isLenderFeeFilteringEnabled: boolean): IDealerFee[] =>
    isLenderFeeFilteringEnabled ? fees.filter(countsInCalculation) : fees;

export function getGovernmentFees(state: OfferReduxBaseState): GovernmentFees {
    const offerType = getCurrentOfferType(state);
    return getGovernmentFeesByOfferType(state, offerType);
}

export function getGovernmentFeesByOfferType(state: OfferReduxBaseState, offerType: OfferType): GovernmentFees {
    const defaultFees = { total: 0, cappedFeeTotal: 0, upfrontFeeTotal: 0, fee: { taxFee: [] as ItemizedGovernmentFee[] } };
    const offer = getOfferTerm(state, offerType);
    return offer?.fees ?? defaultFees;
}

export function getGovernmentUpfrontFeeTotal(state: OfferReduxBaseState): number {
    const govFees = getGovernmentFees(state);
    return govFees?.upfrontFeeTotal || 0;
}

export function getDealerDueAtSigningFees(state: OfferReduxBaseState): IDealerFee[] {
    const allDealerFees = getDealerFees(state);
    const dueAtSigningFees: IDealerFee[] = [];
    allDealerFees.forEach((fee) => {
        if (!fee.dealerFeeCapped) {
            dueAtSigningFees.push(fee);
        }
    });
    return dueAtSigningFees;
}

/**
 * @deprecated use getDiaDueAtSigning
 */
export function getDueAtSigningFromDueInAdvance(state: OfferReduxBaseState) {
    return getDiaDueAtSigning(state);
}

/**
 * @deprecated use getPaidAtSigning
 */
export function getDueAtSigningPaidByFromDueInAdvance(state: OfferReduxBaseState) {
    return getPaidAtSigning(state);
}

export function getDueAtSigning(state: any, offerType?: OfferType): number {
    const term = getSelectedTerm(state, offerType);

    if (term.dueAtSigning == null) return 0;

    // adjust the value for -ve trade that will be paid by cash
    const tradeInAmountOwed = getTradeInAmountOwed(state);
    const tradeInValue = getTradeInValue(state);
    const isNegativeTradeIn = tradeInValue < tradeInAmountOwed;
    const isFinanceNegativeTradeIn = isNegativeTradeIn && shouldIncludeTradeIn(state);

    const tradeDiff = isNegativeTradeIn && !isFinanceNegativeTradeIn ? tradeInAmountOwed - tradeInValue : 0;
    const dueAtSigning = term.dueAtSigning || 0;
    return dueAtSigning + tradeDiff;
}

export const getTotalFee = (state: OfferReduxBaseState, offerType?: OfferType): number => {
    const type = offerType || getCurrentOfferType(state);
    const term = getOfferTerm(state, type);
    return term.totalFee || 0;
};

export function getTotalUnifiFees(state: OfferReduxBaseState, offerType?: OfferType) {
    const type = offerType || getCurrentOfferType(state);
    if (type === CASH) {
        const { dealerFeesTotal = 0, dmvFees = 0 } = getOfferDetails(state, CASH);
        return dealerFeesTotal + dmvFees;
    } else {
        const selectedTermIdForOfferType = getTermIdForOfferType(state.offer, type);
        const term = getTerm(state, selectedTermIdForOfferType);
        return getTotalUnifiFeesFromTerm(term) || 0;
    }
}

export function getTotalUnifiFeesFromTerm(term: any) {
    // "Unifi" because this seems to reflect what we show for fees on SDP deal summary which
    // should match unifi
    return (
        getDealerFeesTotalFromTerm(term) +
        getAcquisitionFeeFromTerm(term) +
        getSecurityDepositFromTerm(term) +
        getDmvFeesFromTerm(term)
    );
}

export function getDmvFeesFromTerm(term: PaymentTerm): number {
    return term?.fees?.total || 0;
}

export function getAcquisitionFeeFromTerm(term: OfferReduxBaseState): number {
    return term?.acqLoanFee || 0;
}

export function getSecurityDepositFromTerm(term: PaymentTerm): number {
    return term.securityDeposit || 0;
}

export const hasDmvFees = (state: OfferReduxBaseState): boolean => {
    return getGovernmentFees(state).total > 0;
};

export const getAcquisitionFee = (termsForOffer: any): string => {
    if (termsForOffer?.acqLoanFee) {
        return termsForOffer.acqLoanFee.toString();
    }

    if (termsForOffer?.dealerFees) {
        const acquisitionFeeFromDealerFees = getDealerAcquisitionFeeFromTerm(termsForOffer);

        if (acquisitionFeeFromDealerFees) {
            return acquisitionFeeFromDealerFees.dealerFeeAmount.toString();
        }
        return '0';
    }
    return '0';
};

export function getDealerAcquisitionFeeByOfferType(state: OfferReduxBaseState, offerType: OfferType): any {
    const term = getSelectedTermByOfferType(state, offerType);
    return getDealerAcquisitionFeeFromTerm(term);
}

export function getDealerAcquisitionFeeAmountByOfferType(state: OfferReduxBaseState, offerType: OfferType): any {
    const dealerFee = getDealerAcquisitionFeeByOfferType(state, offerType);
    return dealerFee?.dealerFeeAmount ?? 0;
}

export function getDealerAcquisitionFeeCappedByOfferType(state: OfferReduxBaseState, offerType: OfferType): any {
    const dealerFee = getDealerAcquisitionFeeByOfferType(state, offerType);
    return dealerFee?.dealerFeeCapped ?? false;
}

export function getDealerAcquisitionFeeUpfrontByOfferType(state: OfferReduxBaseState, offerType: OfferType): any {
    const dealerFee = getDealerAcquisitionFeeByOfferType(state, offerType);
    if (dealerFee !== undefined) {
        return !dealerFee.dealerFeeCapped;
    } else {
        return false;
    }
}

/**
 * Get whether the acquisition fee is up-front (NOT capitalized)
 * @param state redux state
 * @param offerType offer type
 * @param enableFallback (default: false) Pass true to enable fallback to selected term value
 * @returns true if acquisition fee is up-front (NOT capitalized)
 */
export const getAcqFeeUpFrontOverrideByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    enableFallback = false
): boolean | undefined => {
    const { acqFeeUpFrontOverride } = getOfferDetails(state, offerType);
    if (acqFeeUpFrontOverride != null) return acqFeeUpFrontOverride;
    if (!enableFallback) return undefined;
    const acqFeeUpFront = getDealerAcquisitionFeeUpfrontByOfferType(state, offerType);
    return acqFeeUpFront;
};

/**
 * Get acquisition fee override
 * @param state redux state
 * @param enableFallback (default: false) Pass true to fallback to acquisition fee from dealer fees
 * @returns Acquisition fee value
 */
export const getAcqFeeOverride = (state: OfferReduxBaseState, enableFallback = false): number | undefined => {
    const offerType = getCurrentOfferType(state);
    return getAcqFeeOverrideByOfferType(state, offerType, enableFallback);
};

/**
 * Get acquisition fee override by offer type (hint: always lease)
 * @param state redux state
 * @param offerType offer type
 * @param enableFallback (default: false) Pass true to fallback to acquisition fee from dealer fees
 * @returns Acquisition fee
 */
export const getAcqFeeOverrideByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    enableFallback = false
): number | undefined => {
    const { acqFeeOverride } = getOfferDetails(state, offerType);
    if (acqFeeOverride != null) return acqFeeOverride;
    if (!enableFallback) return undefined;
    const acqFeeFromTerm = getDealerAcquisitionFeeAmountByOfferType(state, offerType);
    return acqFeeFromTerm;
};

/**
 * Whether the acquisition fee on the selected term was entered by the dealer rather than supplied by the
 * lender program, which is what "the dealer overrode it" means for the override indicator.
 *
 * Reads Payment Services' `isManualFee`, not the amount: once PS applies the override it echoes the entered
 * amount back as the term's fee, so the two are the same number on every later load (DE773502). With no fee
 * entry to report on, the program supplies no acquisition fee and `acqFeeOverride` answers instead.
 */
export const isAcqFeeStillDealerEntered = (state: OfferReduxBaseState, offerType: OfferType): boolean => {
    const acqFee = getDealerAcquisitionFeeByOfferType(state, offerType);
    if (acqFee != null) return Boolean(acqFee.isManualFee);
    return getOfferDetails(state, offerType).acqFeeOverride != null;
};

/**
 * Get whether the acquisition fee is up-front (NOT capitalized)
 * @param state Redux state
 * @param enableFallback (default: false) Pass true to enable fallback to selected term value
 * @returns true if acquisition fee is up-front (NOT capitalized)
 */
export const getAcqFeeUpFrontOverride = (state: OfferReduxBaseState, enableFallback = false): boolean | undefined => {
    const offerType = getCurrentOfferType(state);
    return getAcqFeeUpFrontOverrideByOfferType(state, offerType, enableFallback);
};

/**
 * Get the security desposite override value by offer type. (hint: always lease)
 * @param state redux state
 * @param offerType offer type
 * @param enableFallback (default: false) Pass true to enable falling back to the selected term's value.
 * @returns Security deposit value
 */
export const getSecurityDepositOverrideByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    enableFallback = false
): number | undefined => {
    const { securityDepositOverride } = getOfferDetails(state, offerType);
    if (securityDepositOverride != null) return securityDepositOverride;

    if (!enableFallback) return undefined;

    if (offerType !== LEASE) return undefined;
    const term = getSelectedTermByOfferType(state, offerType);
    return term?.securityDeposit ?? 0;
};

/**
 * Get security deposit override from offer type (hint: always lease)
 * @param state redux state
 * @param enableFallback (default: false) Pass true to enable falling back to the selected term's value.
 * @returns Security deposit value
 */
export const getSecurityDepositOverride = (state: OfferReduxBaseState, enableFallback = false): number | undefined => {
    const offerType = getCurrentOfferType(state);
    return getSecurityDepositOverrideByOfferType(state, offerType, enableFallback);
};

export const getMaxResidualizedMsrpOverrideByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    enableFallback = false
): number | undefined => {
    const { maxResidualizedMsrp } = getOfferDetails(state, offerType);
    if (maxResidualizedMsrp != null) return maxResidualizedMsrp;

    if (!enableFallback) return undefined;

    if (offerType !== LEASE) return undefined;
    const term = getSelectedTermByOfferType(state, offerType);
    return term?.maxResidualizedMsrp ?? 0;
};

export const getMaxResidualizedMsrpAppliedOverrideByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    enableFallback = false
): boolean | undefined => {
    const { maxResidualizedMsrpApplied } = getOfferDetails(state, offerType);
    if (maxResidualizedMsrpApplied != null) return maxResidualizedMsrpApplied;

    if (!enableFallback) return undefined;

    if (offerType !== LEASE) return undefined;
    const term = getSelectedTermByOfferType(state, offerType);
    return term?.maxResidualizedMsrpApplied ?? false;
};

export const getMaxResidualizedMsrpOverride = (state: OfferReduxBaseState, enableFallback = false): number | undefined => {
    const offerType = getCurrentOfferType(state);
    return getMaxResidualizedMsrpOverrideByOfferType(state, offerType, enableFallback);
};

export const getMaxResidualizedMsrpAppliedOverride = (state: OfferReduxBaseState, enableFallback = false): boolean | undefined => {
    const offerType = getCurrentOfferType(state);
    return getMaxResidualizedMsrpAppliedOverrideByOfferType(state, offerType, enableFallback);
};

export const getMileageChargeOverride = (state: OfferReduxBaseState, enableFallback = false): number | undefined => {
    const offerType = getCurrentOfferType(state);
    return getMileageChargeOverrideByOfferType(state, offerType, enableFallback);
};

export const getMileageChargeOverrideByOfferType = (
    state: OfferReduxBaseState,
    offerType: OfferType,
    enableFallback = false
): number | undefined => {
    const { mileageChargeOverride } = getOfferDetails(state, offerType);
    if (mileageChargeOverride != null) return mileageChargeOverride;

    if (!enableFallback) return undefined;

    if (offerType !== LEASE) return undefined;
    const term = getSelectedTermByOfferType(state, offerType);
    return Number(term?.excessMileageCharge ?? 0);
};

export const getFeesOverride = (state: OfferReduxBaseState, offerType?: OfferType): FeeOverride[] | undefined => {
    const targetOfferType = offerType || getCurrentOfferType(state);
    const selectedOfferDetails = getOfferDetails(state, targetOfferType);
    return selectedOfferDetails.feesOverride;
};

export const getGovernmentFeesOverride = (state: OfferReduxBaseState, offerType?: OfferType): any | undefined => {
    const feesOverrides = getFeesOverride(state, offerType);
    return feesOverrides?.filter((feesOverride: any) => feesOverride.category === FeesCategoriesType.GOVERNMENT);
};

export const getCapitalizedFeesByOfferType = (state: OfferReduxBaseState, offerType: OfferType) => {
    const selectedTerm = getSelectedTerm(state, offerType);
    return selectedTerm?.capitalizedFeesTotal;
};

export const getCappedFeeTotal = (state: OfferReduxBaseState) => {
    const offerType = getCurrentOfferType(state);
    const selectedTerm = getSelectedTerm(state, offerType);
    return selectedTerm?.fees?.cappedFeeTotal;
};
